import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
import { useBoolean } from 'src/hooks/use-boolean';
// router
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/routes/paths';
// components
import FormProvider, { RHFSwitch, RHFTextField, RHFSelect } from 'src/components/hook-form';
// redux
import { setError } from 'src/redux/slices/error';
// utils
import { fStringInputWithoutSpecialCharacters } from 'src/utils/format-input';
import { useLocales } from '../../../locales';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
// DTO
import { ReportsService, ReportTemplateDto, ReportType } from '../../../api';

// ----------------------------------------------------------------------

type props = {
  openState: ReturnType<typeof useBoolean>;
};

export default function ReportEditDialog({ openState }: props) {
  const mdUp = useResponsive('up', 'md');

  const { t } = useLocales();

  const navigate = useNavigate();

  const originalReport = useSelector((state) => state.report.report);

  const [loading, setLoading] = useState(false);

  const newSchema = Yup.object().shape({
    name: Yup.string().required(),
    description: Yup.string(),
    isGlobal: Yup.boolean(),
    type: Yup.mixed(),
  });

  const defaultValues = useMemo(
    () =>
      ({
        name: originalReport.name,
        description: originalReport.description,
        isGlobal: originalReport.isGlobal,
        type: originalReport.type,
      } as any),
    [originalReport]
  );

  const methods = useForm({
    resolver: yupResolver(newSchema),
    defaultValues,
  });

  const { reset, setValue, watch } = methods;

  const values = watch();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const dispatch = useDispatch();

  const createReport = async () => {
    try {
      if (values.name === '') {
        throw Error(t('Name is not set'));
      }
      if (values.description && values.description?.length > 500) {
        throw Error(t('Description is too long'));
      }
      if (values.type === undefined) {
        throw Error(t('Type is not set'));
      }

      const dto: ReportTemplateDto = {
        ...originalReport,
        ...values,
      };

      setLoading(true);
      await ReportsService.updateReportTemplate({ body: dto });
      setLoading(false);
      navigate(paths.report.list);
    } catch (error) {
      dispatch(setError(error));
      setLoading(false);
    }
  };

  const renderDialog = (
    <Dialog
      fullWidth
      maxWidth={mdUp ? 'sm' : 'md'}
      open={openState.value}
      onClose={openState.onFalse}
      slotProps={{}}
    >
      <DialogTitle>{t('Save report')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ my: 1 }}>
          {t('Here you can change the basic information of the report.')}
        </DialogContentText>
        <Stack spacing={1.5}>
          <Stack>
            <RHFTextField
              name="name"
              sx={{ my: 1 }}
              label={t('Report name')}
              type="text"
              value={values.name}
              onChange={(event) =>
                fStringInputWithoutSpecialCharacters({
                  name: 'name',
                  setValue,
                  value: event.target.value,
                })
              }
            />
          </Stack>

          <Stack>
            <RHFSelect
              name="type"
              value={values.type ?? -1}
              size="medium"
              label={t('Select the report type')}
            >
              <MenuItem value={-1} disabled>
                {t('Please select the report type')}
              </MenuItem>
              {Object.keys(ReportType).map((x) => (
                <MenuItem key={x} value={x}>
                  {t(x)}
                </MenuItem>
              ))}
            </RHFSelect>
          </Stack>

          <Stack>
            <RHFTextField
              name="description"
              sx={{ my: 1 }}
              label={t('Short description')}
              type="text"
              value={values.description}
            />
          </Stack>

          <Stack>
            <RHFSwitch
              name="isGlobal"
              value={values.isGlobal}
              label={t('Visible to all administrative users')}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={openState.onFalse}>{t('Cancel')}</Button>
        <Button
          color="primary"
          onClick={() => createReport()}
          variant="contained"
          loading={loading}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return <FormProvider methods={methods}>{renderDialog}</FormProvider>;
}
